/**
 * Authentication state.
 *
 * Zustand holds only genuinely global, non-server state: who is signed in and
 * whether the initial auth check has finished. Everything fetched from the API
 * lives in TanStack Query instead, so there is one cache rather than two.
 */

import { create } from 'zustand';

export const useAuthStore = create((set) => ({
  /** The Firebase user object, or null. */
  firebaseUser: null,
  /** The MongoDB profile from /auth/sync, or null. */
  profile: null,
  /** False until the first onAuthStateChanged callback has run. */
  initialized: false,
  /** True while a sign-in or sign-up request is in flight. */
  busy: false,
  /**
   * A credential kept aside when a user tried a provider whose email already
   * belongs to another sign-in method. Linked once they authenticate.
   */
  pendingLinkCredential: null,

  setFirebaseUser: (firebaseUser) => set({ firebaseUser }),
  setProfile: (profile) => set({ profile }),
  setInitialized: (initialized) => set({ initialized }),
  setBusy: (busy) => set({ busy }),
  setPendingLinkCredential: (pendingLinkCredential) => set({ pendingLinkCredential }),

  /** Clears every trace of the previous session. */
  reset: () =>
    set({
      firebaseUser: null,
      profile: null,
      busy: false,
      pendingLinkCredential: null,
    }),
}));

/** Convenience selectors. */
export const selectIsAuthenticated = (state) => Boolean(state.firebaseUser);
export const selectUserId = (state) => state.profile?.id ?? null;
export const selectIsAdmin = (state) => state.profile?.role === 'admin';
export const selectIsModerator = (state) =>
  state.profile?.role === 'admin' || state.profile?.role === 'moderator';
